/** @file renameNode: checks a new node name, dispatches one `node.rename`, moves the selection with it. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import { USER_NAME_PATTERN } from '@zilo/pose3d-commands/validate/userNamePattern';
import type { EditorStore } from '../../state/editorTypes';
import { treeUi } from './treeUi';

/** The batch for one rename: `node.rename { name, to }` (CR-008). */
function renameBatch(name: string, to: string): Command[] {
  return [{ name: 'node.rename', input: { name, to } }];
}

/** The message for a name that cannot be used, or null: the command's own rule (`USER_NAME_PATTERN`), not taken by another node. */
function nameProblem(nodes: Record<string, unknown>, from: string, to: string): string | null {
  if (!USER_NAME_PATTERN.test(to)) return 'Use an ASCII name: a letter or _, then letters, digits or _';
  if (to !== from && Object.hasOwn(nodes, to)) return `A node named "${to}" exists`;
  return null;
}

/**
 * Renames `from` to `to`. Returns null on success (or when the name did not change), else the message to show inline
 * (a local check first, then the command's own validation error). On success the selection follows the new name.
 */
export async function renameNode(store: EditorStore, from: string, to: string): Promise<string | null> {
  const s = store.getState();
  if (to === from) return null;
  const problem = nameProblem(s.puppet.doc?.puppet.nodes ?? {}, from, to);
  if (problem !== null) return problem;
  let replies;
  try {
    replies = await s.dispatch(renameBatch(from, to), `rename ${from} to ${to}`);
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
  const failed = replies.find((r) => !r.ok);
  if (failed?.ok === false) return failed.error.message;
  const now = store.getState();
  now.select({ nodes: now.selection.nodes.map((n) => (n === from ? to : n)) });
  treeUi(store).getState().renamed(from, to);
  return null;
}
